import { useEffect, useRef, useState } from 'react'
import styles from '../styles/shell.module.scss'

export interface InlineNameEditProps {
  value: string
  onCommit: (next: string) => void
  ariaLabel: string
  title?: string
  /** Fixed text pinned after the field (a file extension, say) — shown
   * but never editable. */
  suffix?: string
  /** Applied to the typed value before it reaches ``onCommit``; used
   * where the name will become a file name. Defaults to a trim. */
  transform?: (raw: string) => string
}

/**
 * Click-to-edit label. Enter commits, Escape cancels, blur commits (the
 * user clicked away from a field they opened deliberately).
 *
 * Typography is inherited from the parent on purpose — the caller wraps
 * it in whatever element carries the font, so a project title and a clip
 * row can share the component without a specificity fight between two
 * CSS modules.
 */
export function InlineNameEdit({
  value,
  onCommit,
  ariaLabel,
  title,
  suffix,
  transform,
}: InlineNameEditProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  // Escape has to beat the blur that follows it, or cancelling would
  // still commit.
  const cancelledRef = useRef(false)

  useEffect(() => {
    if (!editing) return
    const el = inputRef.current
    if (!el) return
    el.focus()
    el.select()
  }, [editing])

  const commit = () => {
    if (cancelledRef.current) return
    setEditing(false)
    const next = transform ? transform(draft) : draft.trim()
    if (next && next !== value) onCommit(next)
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={styles.inlineNameButton}
        onClick={(e) => {
          // The row underneath can be clickable (plays the clip).
          e.stopPropagation()
          setDraft(value)
          cancelledRef.current = false
          setEditing(true)
        }}
        title={title ?? 'Clique para renomear'}
        aria-label={ariaLabel}
      >
        <span className={styles.inlineNameText}>{value}</span>
        {suffix ? <span className={styles.inlineNameSuffix}>{suffix}</span> : null}
      </button>
    )
  }

  return (
    <span className={styles.inlineNameWrap}>
      <input
        ref={inputRef}
        className={styles.inlineNameInput}
        value={draft}
        aria-label={ariaLabel}
        spellCheck={false}
        onChange={(e) => {
          setDraft(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            cancelledRef.current = true
            setEditing(false)
          }
        }}
        onBlur={commit}
        onClick={(e) => {
          e.stopPropagation()
        }}
      />
      {suffix ? <span className={styles.inlineNameSuffix}>{suffix}</span> : null}
    </span>
  )
}
